import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { apiFetch } from '../../services/api';
import AdminShell from './AdminShell';

const LLM_PROVIDERS = ['anthropic', 'openai', 'google', 'mistral', 'cohere'];

const JSONB_FIELDS = [
  { key: 'can_do',             label: 'Can Do' },
  { key: 'cannot_do',          label: 'Cannot Do' },
  { key: 'task_types',         label: 'Task Types' },
  { key: 'input_requirements', label: 'Input Requirements' },
  { key: 'output_types',       label: 'Output Types' },
];

const EMPTY_FORM = {
  agent_key:                '',
  name:                     '',
  llm_provider_code:        '',
  llm_model_code:           '',
  recommended_input_token:  '',
  recommended_output_token: '',
  short_description:        '',
  planner_summary:          '',
  execution_prompt_md:      '',
  is_active:                true,
  can_do:                   '[]',
  cannot_do:                '[]',
  task_types:               '[]',
  input_requirements:       '[]',
  output_types:             '[]',
};

function useMarked() {
  const [ready, setReady] = React.useState(!!window.marked);
  React.useEffect(() => {
    if (window.marked) return;
    const s = document.createElement('script');
    s.src = 'https://cdnjs.cloudflare.com/ajax/libs/marked/9.1.6/marked.min.js';
    s.onload = () => setReady(true);
    document.head.appendChild(s);
  }, []);
  return ready;
}

function isValidJson(str) {
  try { JSON.parse(str); return true; }
  catch { return false; }
}

const crumbs = [
  { label: 'Agents', href: '/admin/agents' },
  { label: 'New Agent' },
];

export default function AdminAgentNewPage() {
  const { user }    = useAuth();
  const navigate    = useNavigate();
  const markedReady = useMarked();

  const [form,       setForm]       = useState(EMPTY_FORM);
  const [jsonErrors, setJsonErrors] = useState({});
  const [saving,     setSaving]     = useState(false);
  const [saveError,  setSaveError]  = useState(null);
  const [promptTab,  setPromptTab]  = useState('edit');

  React.useEffect(() => {
    if (user && !user.isSuperAdmin) navigate('/dashboard', { replace: true });
  }, [user, navigate]);

  function setField(key, value) {
    setForm(f => ({ ...f, [key]: value }));
  }

  function validateJsonFields() {
    const errors = {};
    for (const { key, label } of JSONB_FIELDS) {
      if (!isValidJson(form[key])) {
        errors[key] = `${label}: invalid JSON`;
      }
    }
    setJsonErrors(errors);
    return Object.keys(errors).length === 0;
  }

  async function handleCreate() {
    if (!form.name.trim())      return setSaveError('Name is required.');
    if (!form.agent_key.trim()) return setSaveError('Agent key is required.');
    if (!form.execution_prompt_md.trim())
      return setSaveError('Execution prompt is required.');
    if (!validateJsonFields()) return;

    setSaving(true);
    setSaveError(null);
    try {
      const payload = { ...form };
      for (const { key } of JSONB_FIELDS) {
        payload[key] = JSON.parse(form[key]);
      }
      // Remove empty optional strings so backend receives null
      ['llm_provider_code','llm_model_code',
       'recommended_input_token','recommended_output_token',
       'short_description','planner_summary'].forEach(k => {
        if (payload[k] === '') payload[k] = null;
      });
      const created = await apiFetch('/api/admin/agents', {
        method: 'POST',
        body: JSON.stringify(payload),
      });
      navigate(`/admin/agents/${created.agent_id}`, { replace: true });
    } catch (e) {
      setSaveError(e?.message ?? 'Create failed. Please try again.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <AdminShell crumbs={crumbs}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 280px',
        gap: '28px', alignItems: 'start' }}>

        {/* ── Main form ── */}
        <div>
          <h1 style={{ fontFamily: 'var(--serif)', fontSize: '24px',
            fontWeight: '400', marginBottom: '20px' }}>
            New Agent
          </h1>

          <div style={{
            background: 'var(--white)', border: '1px solid var(--paper-3)',
            borderRadius: 'var(--r)', padding: '20px 24px',
          }}>

            {/* Row 1: name + key */}
            <div style={{ display: 'flex', gap: '12px', marginBottom: '16px',
              flexWrap: 'wrap' }}>
              <div style={{ flex: 2, minWidth: '180px' }}>
                <label style={labelStyle}>Name <Req /></label>
                <input value={form.name}
                  onChange={e => setField('name', e.target.value)}
                  placeholder="e.g. Marketing Agent"
                  style={inputStyle} />
              </div>
              <div style={{ flex: 1, minWidth: '140px' }}>
                <label style={labelStyle}>Agent Key <Req /></label>
                <input value={form.agent_key}
                  onChange={e => setField('agent_key',
                    e.target.value.toLowerCase().replace(/\s+/g, '_'))}
                  placeholder="e.g. marketing_agent"
                  style={{ ...inputStyle, fontFamily: 'monospace' }} />
              </div>
            </div>

            {/* Row 2: provider + model + tokens */}
            <div style={{ display: 'flex', gap: '12px', marginBottom: '16px',
              flexWrap: 'wrap' }}>
              <div style={{ flex: 1, minWidth: '130px' }}>
                <label style={labelStyle}>LLM Provider</label>
                <select value={form.llm_provider_code}
                  onChange={e => setField('llm_provider_code', e.target.value)}
                  style={inputStyle}>
                  <option value="">— none —</option>
                  {LLM_PROVIDERS.map(p => (
                    <option key={p} value={p}>{p}</option>
                  ))}
                </select>
              </div>
              <div style={{ flex: 2, minWidth: '180px' }}>
                <label style={labelStyle}>Model Code</label>
                <input value={form.llm_model_code}
                  onChange={e => setField('llm_model_code', e.target.value)}
                  placeholder="e.g. claude-haiku-4-5"
                  style={{ ...inputStyle, fontFamily: 'monospace' }} />
              </div>
              <div style={{ minWidth: '100px' }}>
                <label style={labelStyle}>Input Tokens</label>
                <input value={form.recommended_input_token}
                  onChange={e => setField('recommended_input_token', e.target.value)}
                  placeholder="e.g. 4096"
                  style={inputStyle} />
              </div>
              <div style={{ minWidth: '100px' }}>
                <label style={labelStyle}>Output Tokens</label>
                <input value={form.recommended_output_token}
                  onChange={e => setField('recommended_output_token', e.target.value)}
                  placeholder="e.g. 2048"
                  style={inputStyle} />
              </div>
            </div>

            {/* Short description */}
            <div style={{ marginBottom: '16px' }}>
              <label style={labelStyle}>Short Description</label>
              <input value={form.short_description}
                onChange={e => setField('short_description', e.target.value)}
                placeholder="One-line description shown in lists"
                style={inputStyle} />
            </div>

            {/* Planner summary */}
            <div style={{ marginBottom: '16px' }}>
              <label style={labelStyle}>Planner Summary</label>
              <textarea value={form.planner_summary}
                onChange={e => setField('planner_summary', e.target.value)}
                rows={3}
                placeholder="How the planner should understand and use this agent…"
                style={{ ...inputStyle, resize: 'vertical', lineHeight: 1.5 }} />
            </div>

            {/* JSONB fields */}
            <div style={{ marginBottom: '4px' }}>
              <p style={{ fontSize: '11px', color: 'var(--ink-mute)',
                marginBottom: '12px' }}>
                JSON arrays below — e.g. <code style={{ fontFamily: 'monospace' }}>
                ["item one", "item two"]</code>
              </p>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr',
              gap: '12px', marginBottom: '16px' }}>
              {JSONB_FIELDS.map(({ key, label }) => (
                <div key={key}>
                  <label style={labelStyle}>{label}</label>
                  <textarea
                    value={form[key]}
                    onChange={e => {
                      setField(key, e.target.value);
                      if (jsonErrors[key]) {
                        setJsonErrors(prev => {
                          const next = { ...prev }; delete next[key]; return next;
                        });
                      }
                    }}
                    rows={5}
                    style={{
                      ...inputStyle, resize: 'vertical',
                      fontFamily: 'monospace', fontSize: '12px', lineHeight: 1.5,
                      borderColor: jsonErrors[key]
                        ? 'rgba(200,86,58,0.6)' : undefined,
                    }}
                  />
                  {jsonErrors[key] && (
                    <p style={{ fontSize: '11px', color: 'var(--accent)',
                      marginTop: '3px' }}>
                      {jsonErrors[key]}
                    </p>
                  )}
                </div>
              ))}
            </div>

            {/* Execution prompt */}
            <div style={{ marginBottom: '16px' }}>
              <label style={labelStyle}>Execution Prompt (Markdown) <Req /></label>
              <div style={{ display: 'flex', gap: '4px', marginBottom: '8px' }}>
                {['edit', 'preview'].map(tab => (
                  <button key={tab}
                    onClick={() => setPromptTab(tab)}
                    style={{
                      padding: '4px 12px', fontSize: '12px', borderRadius: '6px',
                      border: '1px solid var(--paper-3)', cursor: 'pointer',
                      fontFamily: 'var(--sans)', textTransform: 'capitalize',
                      background: promptTab === tab ? 'var(--ink)' : 'var(--white)',
                      color:      promptTab === tab ? 'var(--paper)' : 'var(--ink-soft)',
                    }}>
                    {tab}
                  </button>
                ))}
              </div>
              {promptTab === 'preview' ? (
                <div
                  style={{
                    minHeight: '280px', padding: '16px',
                    background: 'var(--paper-2)', borderRadius: '6px',
                    border: '1px solid var(--paper-3)',
                    fontSize: '13px', lineHeight: 1.7, color: 'var(--ink)',
                    overflowY: 'auto',
                  }}
                  dangerouslySetInnerHTML={{
                    __html: markedReady && window.marked
                      ? window.marked.parse(form.execution_prompt_md || '',
                          { gfm: true, breaks: true })
                      : `<pre style="white-space:pre-wrap">${form.execution_prompt_md}</pre>`
                  }}
                />
              ) : (
                <textarea
                  value={form.execution_prompt_md}
                  onChange={e => setField('execution_prompt_md', e.target.value)}
                  rows={20}
                  placeholder="You are the [Name] agent…"
                  style={{
                    ...inputStyle, resize: 'vertical',
                    fontFamily: 'monospace', fontSize: '12px',
                    lineHeight: 1.6, minHeight: '280px',
                  }}
                />
              )}
            </div>

            {/* Active toggle */}
            <div style={{ marginBottom: '20px', display: 'flex',
              alignItems: 'center', gap: '10px' }}>
              <input type="checkbox" id="is_active_new"
                checked={form.is_active}
                onChange={e => setField('is_active', e.target.checked)}
                style={{ width: '14px', height: '14px', cursor: 'pointer' }} />
              <label htmlFor="is_active_new"
                style={{ fontSize: '13px', cursor: 'pointer',
                  color: 'var(--ink-soft)' }}>
                Active on create
              </label>
            </div>

            {saveError && (
              <p style={{ marginBottom: '12px', fontSize: '12px',
                color: 'var(--accent)', background: 'rgba(200,86,58,0.08)',
                padding: '8px 12px', borderRadius: '6px' }}>
                {saveError}
              </p>
            )}

            <div style={{ display: 'flex', gap: '8px' }}>
              <button onClick={handleCreate} disabled={saving}
                style={{ ...primaryBtn, opacity: saving ? 0.6 : 1 }}>
                {saving ? 'Creating…' : '✓ Create agent'}
              </button>
              <button onClick={() => navigate('/admin/agents')}
                style={secondaryBtn}>
                Cancel
              </button>
            </div>
          </div>
        </div>

        {/* ── Sidebar ── */}
        <div style={{
          background: 'var(--white)', border: '1px solid var(--paper-3)',
          borderRadius: 'var(--r)', padding: '16px 18px',
        }}>
          <div style={{
            fontSize: '10px', fontWeight: '600', letterSpacing: '0.1em',
            textTransform: 'uppercase', color: 'var(--ink-mute)',
            marginBottom: '12px', paddingBottom: '8px',
            borderBottom: '1px solid var(--paper-3)',
          }}>
            Tips
          </div>
          <ul style={{ margin: 0, paddingLeft: '16px',
            listStyle: 'disc', color: 'var(--ink-mute)' }}>
            {[
              'agent_key must be unique and lowercase with underscores.',
              'JSONB fields expect valid JSON arrays — use [] for empty.',
              'The execution prompt is what the LLM receives at runtime.',
              'You can deactivate an agent without deleting it.',
            ].map((tip, i) => (
              <li key={i} style={{ fontSize: '12px', lineHeight: 1.6,
                marginBottom: '8px', color: 'var(--ink-soft)' }}>
                {tip}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </AdminShell>
  );
}

function Req() {
  return <span style={{ color: 'var(--accent)', marginLeft: '2px' }}>*</span>;
}

const labelStyle = {
  display: 'block', fontSize: '12px', fontWeight: '500',
  color: 'var(--ink-mute)', marginBottom: '6px', letterSpacing: '0.03em',
};

const inputStyle = {
  width: '100%', padding: '8px 12px', fontSize: '13px',
  border: '1px solid var(--paper-3)', borderRadius: '6px',
  fontFamily: 'var(--sans)', background: 'var(--white)',
  color: 'var(--ink)', boxSizing: 'border-box',
};

const primaryBtn = {
  padding: '9px 18px', background: 'var(--accent)', color: '#fff',
  border: 'none', borderRadius: '6px', fontFamily: 'var(--sans)',
  fontSize: '13px', fontWeight: '600', cursor: 'pointer', whiteSpace: 'nowrap',
};

const secondaryBtn = {
  padding: '9px 18px', background: 'var(--white)', color: 'var(--ink)',
  border: '1px solid var(--paper-3)', borderRadius: '6px',
  fontFamily: 'var(--sans)', fontSize: '13px', fontWeight: '500',
  cursor: 'pointer', whiteSpace: 'nowrap',
};
